import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { SuppliesOrderStatus } from "@/constants/entity-status";
import {
  SuppliesOrderAttemptsModal,
  SuppliesOrderAttempt,
} from "@/components/modal/SuppliesOrderAttemptsModal";
import { AppColors } from "@/styles/colors";
import { Badge, Button, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { Check, PenLine } from "lucide-react";
import { useMemo, useState } from "react";

const normalizeStatus = (raw?: string) =>
  String(raw ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "");

const canShowReassignAction = (item: any) =>
  item?.estado === SuppliesOrderStatus.NOT_DELIVERED ||
  (!!item?.chofer &&
    item?.estado !== SuppliesOrderStatus.IN_TRANSIT &&
    item?.estado !== SuppliesOrderStatus.DELIVERED);

export interface FuelSlotRequestOrdersTableItem {
  id: number;
  nroPedido?: string;
  cantidad?: number;
  destino?: string;
  lugarDespacho?: string;
  nombreDespacho?: string;
  estado?: string;
  cantidadTotal?: number;
  intentos?: SuppliesOrderAttempt[];
  chofer?: string | null;
}

type FuelOrdersTableComputedItem = FuelSlotRequestOrdersTableItem & {
  litros: string;
  despacho: string;
  status: SuppliesOrderStatus;
  currentIntento: number;
  intentos: SuppliesOrderAttempt[];
};

interface Props {
  orders: FuelSlotRequestOrdersTableItem[];
  loadingConfirmOrderId: number | null;
  onConfirmOrder: (orderId: number) => void;
  showEditOrders?: boolean;
  onEditOrders?: () => void;
  assignDriverBlocked?: boolean;
  reassignDriverBlocked?: boolean;
}

interface ConfirmOrderButtonProps {
  id: number;
  loadingConfirmOrderId: number | null;
  onConfirmOrder: (orderId: number) => void;
}

const ConfirmOrderButton = ({
  id,
  loadingConfirmOrderId,
  onConfirmOrder,
}: ConfirmOrderButtonProps) => {
  return (
    <Button
      height="28px"
      bgColor={AppColors.GREEN_PRIMARY}
      onClick={() => onConfirmOrder(id)}
      loading={loadingConfirmOrderId === id}
      aria-label="Asignar pedido"
      padding="8px 12px"
    >
      <Check size={12} />
      <Text fontWeight={600} fontSize="14px">
        Asignar pedido
      </Text>
    </Button>
  );
};

const ReassignOrderButton = ({
  id,
  loadingConfirmOrderId,
  onConfirmOrder,
}: ConfirmOrderButtonProps) => {
  return (
    <Button
      height="28px"
      bgColor={AppColors.GREEN_PRIMARY}
      onClick={() => onConfirmOrder(id)}
      loading={loadingConfirmOrderId === id}
      aria-label="Reasignar pedido"
      padding="8px 12px"
    >
      <Check size={12} />
      <Text fontWeight={600} fontSize="14px">
        Reasignar pedido
      </Text>
    </Button>
  );
};

interface OrderActionElementsProps {
  item: any;
  loadingConfirmOrderId: number | null;
  onConfirmOrder: (orderId: number) => void;
  onViewAttempts: (item: any) => void;
  assignDriverBlocked?: boolean;
  reassignDriverBlocked?: boolean;
}

const OrderActionElements = ({
  item,
  loadingConfirmOrderId,
  onConfirmOrder,
  onViewAttempts,
  assignDriverBlocked,
  reassignDriverBlocked,
}: OrderActionElementsProps) => {
  const hasAttempts = (item?.intentos ?? []).length >= 1;
  const canReassign = canShowReassignAction(item) && !reassignDriverBlocked;

  if (hasAttempts) {
    if (item?.estado === SuppliesOrderStatus.DELIVERED) return null;

    return (
      <Flex
        alignItems={{ base: "flex-end", md: "center" }}
        justifyContent="flex-end"
        flexDir={{ base: "column", md: "row" }}
        gap="8px"
      >
        <Button
          height="28px"
          padding="8px 12px"
          bgColor={AppColors.BLACK_TEXT_SECONDARY}
          aria-label="Ver intentos"
          onClick={() => onViewAttempts(item)}
        >
          <Text fontWeight={600} fontSize="14px" color={AppColors.WHITE_SECONDARY}>
            Ver intentos
          </Text>
        </Button>

        {canReassign && (
          <ReassignOrderButton
            id={Number(item?.id)}
            loadingConfirmOrderId={loadingConfirmOrderId}
            onConfirmOrder={onConfirmOrder}
          />
        )}
      </Flex>
    );
  }

  if (item?.estado === SuppliesOrderStatus.PENDING) {
    const hasDriverAssigned = !!item?.chofer;

    if (hasDriverAssigned) {
      if (!canReassign) return null;

      return (
        <ReassignOrderButton
          id={Number(item?.id)}
          loadingConfirmOrderId={loadingConfirmOrderId}
          onConfirmOrder={onConfirmOrder}
        />
      );
    }

    if (assignDriverBlocked) return null;

    return (
      <ConfirmOrderButton
        id={Number(item?.id)}
        loadingConfirmOrderId={loadingConfirmOrderId}
        onConfirmOrder={onConfirmOrder}
      />
    );
  }

  return null;
};

export const FuelSlotRequestOrdersTable = ({
  orders,
  loadingConfirmOrderId,
  onConfirmOrder,
  showEditOrders,
  onEditOrders,
  assignDriverBlocked,
  reassignDriverBlocked,
}: Props) => {
  const [openAttempts, setOpenAttempts] = useState(false);
  const [selectedAttemptItem, setSelectedAttemptItem] = useState<
    FuelOrdersTableComputedItem | null
  >(null);

  const items: FuelOrdersTableComputedItem[] = useMemo(() => {
    const mapped = (orders ?? []).map((o) => {
        const nroPedido = o?.nroPedido || `Pedido ${o?.id}`;
        const litros = `${new Intl.NumberFormat("es-AR").format(
          o?.cantidadTotal ?? 0,
        )} litros`;
        const despacho = o?.nombreDespacho || o?.destino || "Sin despacho";
        const status = o?.estado as unknown as SuppliesOrderStatus;
        const intentos = o?.intentos ?? [];
        const maxIntento = Math.max(
          0,
          ...intentos.map((a) => Number(a?.nroIntento ?? 0)),
        );

        let currentIntento = 0;
        if (status === SuppliesOrderStatus.NOT_DELIVERED) {
          currentIntento = maxIntento;
        } else if (status === SuppliesOrderStatus.PENDING && intentos.length >= 1) {
          currentIntento = maxIntento + 1;
        }

        return {
          ...o,
          nroPedido,
          litros,
          despacho,
          status,
          intentos,
          currentIntento,
        };
      }) as FuelOrdersTableComputedItem[];

    return mapped;
  }, [orders]);

  const handleViewAttempts = (item: any) => {
    setSelectedAttemptItem(item);
    setOpenAttempts(true);
  };

  return (
    <Flex flexDir="column" gap="8px" paddingBottom="12px">
      <Flex alignItems="center" justifyContent="space-between">
        <Text fontWeight={700}>Pedidos</Text>

        {showEditOrders && (
          <Button height="28px" onClick={onEditOrders} padding="8px 12px">
            <PenLine size={12} />
            <Text fontWeight={600} fontSize="14px">
              Editar pedidos
            </Text>
          </Button>
        )}
      </Flex>

      <Flex
        flexDir="column"
        border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        borderRadius="8px"
        overflow="hidden"
        maxH="320px"
        overflowY="auto"
      >
        {items.map((item, index) => (
          <Flex
            key={item?.id ?? index}
            alignItems="center"
            justifyContent="flex-start"
            paddingX="20px"
            paddingY="16px"
            borderTop={
              index === 0 ? "none" : `1px solid ${AppColors.WHITE_BORDER_1}`
            }
            width="100%"
          >
            <Grid
              templateColumns={{
                base: "auto 110px 1fr auto",
                md: "auto 150px 1fr auto",
              }}
              alignItems="center"
              width="100%"
              minW={0}
            >
              <GridItem
                borderRight={`1px solid ${AppColors.WHITE_BORDER_1}`}
                paddingRight={{ base: "12px", md: "20px" }}
              >
                <Text fontWeight={700} fontSize="14px">
                  {item?.nroPedido}
                </Text>
              </GridItem>

              <GridItem
                borderRight={`1px solid ${AppColors.WHITE_BORDER_1}`}
                paddingX="24px"
              >
                <Text fontWeight={600} fontSize="14px" color={AppColors.GRAY_TEXT}>
                  {item?.litros}
                </Text>
              </GridItem>

              <GridItem
                borderRight={`1px solid ${AppColors.WHITE_BORDER_1}`}
                paddingX="24px"
                minW={0}
                overflow="hidden"
              >
                <Flex
                  alignItems={{ base: "flex-start", md: "center" }}
                  flexDir={{ base: "column", md: "row" }}
                  columnGap="24px"
                  rowGap="8px"
                  minW={0}
                  flexWrap="wrap"
                >
                  <Text
                    fontWeight={600}
                    fontSize="14px"
                    color={AppColors.GRAY_TEXT}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    title={item?.despacho}
                    textOverflow="ellipsis"
                    minW={0}
                  >
                    {item?.despacho}
                  </Text>

                  <Text
                    fontWeight={600}
                    fontSize="14px"
                    color={AppColors.GRAY_TEXT}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    title={item?.chofer ?? "Sin chofer"}
                    textOverflow="ellipsis"
                    minW={0}
                  >
                    {item?.chofer ? `Chofer: ${item?.chofer}` : "Sin chofer"}
                  </Text>

                  <Flex alignItems="center" gap="8px" flexWrap="wrap" maxW="100%">
                    <RequestStatusBadge status={item?.status} />
                    {item.currentIntento >= 1 && (item?.intentos ?? []).length >= 1 && (
                        <Badge bg={AppColors.YELLOW_SECONDARY} >
                          <Text
                            paddingY="4px"
                            paddingX="8px"
                            fontWeight={600}
                            fontSize="12px"
                            color={AppColors.WHITE_SECONDARY}
                          >
                            Intento {item.currentIntento}
                          </Text>
                        </Badge>
                      )}
                  </Flex>
                </Flex>
              </GridItem>

              <GridItem
                justifySelf="end"
                paddingLeft="12px"
                maxW={{ base: "240px", md: "unset" }}
              >
                <OrderActionElements
                  item={item}
                  loadingConfirmOrderId={loadingConfirmOrderId}
                  onConfirmOrder={onConfirmOrder}
                  onViewAttempts={handleViewAttempts}
                  assignDriverBlocked={assignDriverBlocked}
                  reassignDriverBlocked={reassignDriverBlocked}
                />
              </GridItem>
            </Grid>
          </Flex>
        ))}
      </Flex>

      {selectedAttemptItem && (
        <SuppliesOrderAttemptsModal
          open={openAttempts}
          setOpen={(flag) => {
            setOpenAttempts(flag);
            if (!flag) setSelectedAttemptItem(null);
          }}
          pedidoLabel="N° Pedido: -"
          pedidoId={selectedAttemptItem?.id ?? "-"}
          toneladasOrLitrosLabel={`Litros: ${selectedAttemptItem?.litros ?? "-"}`}
          despachoLabel={`Despacho: ${selectedAttemptItem?.despacho ?? "-"}`}
          attempts={selectedAttemptItem?.intentos ?? []}
          showNextAttemptPlaceholder={
            (selectedAttemptItem?.estado === SuppliesOrderStatus.PENDING ||
              normalizeStatus(selectedAttemptItem?.estado) ===
                normalizeStatus(SuppliesOrderStatus.IN_TRANSIT) ||
              normalizeStatus(selectedAttemptItem?.estado).includes("camino") ||
              normalizeStatus(selectedAttemptItem?.estado).includes("transito")) &&
            (selectedAttemptItem?.intentos ?? []).length >= 1
          }
        />
      )}
    </Flex>
  );
};
